import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";
import type { AppendableEvent, Claim, ClaimDetail, ClaimStatus, FnolInput } from "./types";

const claimKey = (id: string) => ["claim", id];
const OPEN = ["claims", "open"];

/** GET /v1/claims — the advisor's open claims (the desk landing). */
export function useMyOpenClaims() {
  const api = useApi();
  return useQuery({ queryKey: OPEN, queryFn: () => api.get<Claim[]>("/v1/claims") });
}

export function useClaim(id: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: claimKey(id ?? "none"),
    queryFn: () => api.get<ClaimDetail>(`/v1/claims/${id}`),
    enabled: !!id,
  });
}

export function useFnol() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FnolInput) => api.post<Claim>("/v1/claims", input),
    onSettled: () => qc.invalidateQueries({ queryKey: OPEN }),
  });
}

function useClaimRefetch(id: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: claimKey(id) });
    void qc.invalidateQueries({ queryKey: OPEN });
  };
}

/** PATCH /v1/claims/:id — the forward-only transition (carrierClaimRef on SUBMITTED). */
export function useTransition(id: string) {
  const api = useApi();
  const refetch = useClaimRefetch(id);
  return useMutation({
    mutationFn: (input: {
      toStatus: Exclude<ClaimStatus, "REPORTED" | "RESOLVED" | "WITHDRAWN">;
      carrierClaimRef?: string;
    }) => api.patch<Claim>(`/v1/claims/${id}`, input),
    onSettled: refetch,
  });
}

export function useAppendEvent(id: string) {
  const api = useApi();
  const refetch = useClaimRefetch(id);
  return useMutation({
    mutationFn: (input: { eventType: AppendableEvent; payload?: Record<string, unknown> }) =>
      api.post(`/v1/claims/${id}/events`, input),
    onSettled: refetch,
  });
}

export function useResolve(id: string) {
  const api = useApi();
  const refetch = useClaimRefetch(id);
  return useMutation({
    mutationFn: (input: {
      resolution: "RESOLVED" | "WITHDRAWN";
      outcome?: "PAID" | "PARTIALLY_PAID" | "REJECTED";
      outcomeAmount?: string;
      satisfactionScore?: number;
      reason?: string;
    }) => api.post<Claim>(`/v1/claims/${id}/resolve`, input),
    onSettled: refetch,
  });
}
